import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { UserPlus, X, Phone, User, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import api from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Input'
import {
  CompanySearchSelect,
  type V2EmpresaOption,
} from '@/components/CloserLeads/CompanySearchSelect'
import { useEventLeadQuickValidation } from '@/hooks/useEventLeadQuickValidation'
import { useAuthStore } from '@/stores/authStore'
import { formatPhone } from '@/utils/formatPhone'
import { isEventModeRestrictedUser } from '@/utils/userEventMode'
import {
  buildEventLeadQuickPayload,
  validateEventLeadQuickFields,
} from '@/utils/eventLeadQuickForm'

type Props = {
  open: boolean
  onClose: () => void
}

export function EventLeadQuickFormModal({ open, onClose }: Props) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const eventModeRestricted = isEventModeRestrictedUser(user)
  const [contato, setContato] = useState('')
  const [telefone, setTelefone] = useState('')
  const [empresa, setEmpresa] = useState('')
  const [empresaId, setEmpresaId] = useState<string | undefined>()
  const [observacoes, setObservacoes] = useState('')
  const [saving, setSaving] = useState(false)

  const {
    errors,
    clearFieldError,
    resetErrors,
    validateEmpresaSelection,
    validateBeforeSubmit,
  } = useEventLeadQuickValidation({
    contato,
    telefone,
    empresa,
    empresaId,
    skipEmpresaLookup: eventModeRestricted,
  })

  useEffect(() => {
    if (!open) {
      setContato('')
      setTelefone('')
      setEmpresa('')
      setEmpresaId(undefined)
      setObservacoes('')
      resetErrors()
      setSaving(false)
      return
    }

    document.body.style.overflow = 'hidden'
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', onEscape)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onEscape)
    }
  }, [open, onClose, saving, resetErrors])

  const handleEmpresaSelect = async (company: V2EmpresaOption) => {
    setEmpresaId(company.id)
    setEmpresa(company.nome || '')
    clearFieldError('empresa')
    await validateEmpresaSelection(company.id)
  }

  const handleEmpresaChange = (value: string) => {
    setEmpresa(value)
    setEmpresaId(undefined)
    clearFieldError('empresa')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const fieldErrors = validateEventLeadQuickFields({ contato, telefone, empresa, empresaId })
    if (Object.keys(fieldErrors).length > 0) {
      toast.error('Preencha nome, telefone e empresa')
      return
    }

    const { errors: asyncErrors, empresaId: resolvedEmpresaId } = await validateBeforeSubmit()
    if (Object.keys(asyncErrors).length > 0) {
      const message =
        asyncErrors.empresa ||
        asyncErrors.telefone ||
        asyncErrors.contato ||
        'Corrija os campos destacados'
      toast.error(message)
      return
    }

    setSaving(true)

    try {
      await api.post(
        '/closer-leads',
        buildEventLeadQuickPayload({
          contato,
          telefone,
          empresa,
          empresaId: resolvedEmpresaId,
          observacoes,
        }),
      )
      toast.success('Lead cadastrado!')
      onClose()
      navigate('/closer-leads')
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          status?: number
          data?: {
            error?: { message?: string; details?: { field?: string; message: string }[] }
            message?: string
          }
        }
      }
      const status = axiosErr.response?.status
      const respData = axiosErr.response?.data
      const details = respData?.error?.details

      if (status === 409) {
        toast.error(respData?.error?.message ?? respData?.message ?? 'Lead duplicado')
      } else if (status === 400 && Array.isArray(details)) {
        toast.error('Corrija os campos destacados')
      } else {
        toast.error(respData?.error?.message ?? respData?.message ?? 'Erro ao cadastrar lead')
      }
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div
      className='fixed inset-0 z-[100000] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4'
      onClick={() => !saving && onClose()}
    >
      <div
        className='w-full max-w-md overflow-hidden rounded-t-2xl border border-[color:var(--border)] bg-[var(--bg-card)] shadow-2xl sm:rounded-2xl'
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className='flex items-center justify-between border-b border-[color:var(--border)] px-4 py-3'>
          <div className='flex items-center gap-2'>
            <div className='rounded-lg bg-accent/10 p-2 text-accent'>
              <UserPlus size={16} />
            </div>
            <div>
              <h3 className='text-sm font-semibold text-[var(--text-primary)]'>Cadastro rápido</h3>
              <p className='text-[11px] text-text-muted'>
                {eventModeRestricted
                  ? 'Evento · cadastro sem consulta à base de empresas'
                  : 'Evento · validação de empresa e contato'}
              </p>
            </div>
          </div>
          <button
            type='button'
            onClick={onClose}
            disabled={saving}
            className='rounded-lg border border-[color:var(--border)] p-2 text-text-muted transition-colors hover:text-[var(--text-primary)] disabled:opacity-50'
            aria-label='Fechar'
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='space-y-4 p-4'>
          <Input
            label='Nome *'
            value={contato}
            onChange={(e) => {
              setContato(e.target.value)
              clearFieldError('contato')
            }}
            placeholder='Nome do contato'
            icon={<User size={14} />}
            error={errors.contato}
            autoComplete='name'
          />
          <Input
            label='Telefone *'
            value={telefone}
            onChange={(e) => {
              setTelefone(formatPhone(e.target.value))
              clearFieldError('telefone')
            }}
            placeholder='(00) 00000-0000'
            icon={<Phone size={14} />}
            error={errors.telefone}
            inputMode='tel'
            autoComplete='tel'
          />
          {eventModeRestricted ? (
            <Input
              label='Empresa *'
              value={empresa}
              onChange={(e) => {
                setEmpresa(e.target.value)
                setEmpresaId(undefined)
                clearFieldError('empresa')
              }}
              placeholder='Nome da empresa'
              icon={<Building2 size={14} />}
              error={errors.empresa}
              autoComplete='organization'
            />
          ) : (
            <CompanySearchSelect
              value={empresa}
              onSelect={handleEmpresaSelect}
              onChange={handleEmpresaChange}
              onClearSelection={() => setEmpresaId(undefined)}
              error={errors.empresa}
              required
            />
          )}
          <Textarea
            label='Observação'
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder='Anotações do evento (opcional)'
            rows={3}
          />

          <div className='flex gap-2 pt-1'>
            <Button type='button' variant='secondary' className='flex-1' onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button type='submit' variant='primary' className='flex-1' loading={saving}>
              Salvar lead
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
